<template>
  <div class="document-container" style="padding-top: 0">
    <div class="document">
      <div class="header">
        <es-header>
          <template #header>
            <div class="header-title">Election Details</div>
          </template>
          <template #actions>
            <ul>
              <li class="p-narrow no-border">
                <div class="list-content">
                  <router-link
                    :to="{
                      path: '/directMatch/cep/electionsSubmission',
                      query: {
                        locationGroupId,
                        schoolYear,
                      },
                    }"
                    title="Go back to Elections">
                    <font-awesome-icon :icon="['fal', 'angle-double-left']" size="2x"/>
                  </router-link>
                </div>
              </li>
            </ul>
          </template>
        </es-header>
      </div>
      <div class="content" style="padding-left: 1.25rem">
        <div v-if="loading || loadingSummary" style="padding: 6.5rem">
          <es-loader/>
          <div style="padding-top: 1rem">
            Calculating elections data...
          </div>
        </div>
        <div v-else >
          <div v-if="location" class="columns flex-align--center">
            <div class="column">
              <div class="is-flex">
                <div class="is-flex flex-align--center is-size-5 has-text-weight-bold nowrap"
                  style="padding-right: 0.5rem">
                  {{ location.locationName }}
                </div>
                <div
                  v-if="location.stateLocationId"
                  class="is-flex flex-align--center has-text-grey is-italic nowrap"
                  style="padding-right: 1rem">
                  ({{location.stateLocationId}})
                </div>
                <div v-if="location.tags">
                  <div v-for="tag in location.tags" :key="tag.name">
                    <span class="tag is-primary">{{ tag.name }}</span>
                  </div>
                </div>
              </div>
            </div>
            <div class="column profile-overview">
              <div class="columns">
                <div class="column">
                  <div class="column-content">
                    <div class="column-title">
                      {{ location.baseYear ?  location.baseYear : '--'}}
                    </div>
                    <div class="column-subtitle">Base Year</div>
                  </div>
                </div>
                <div class="column">
                  <div class="column-content">
                    <div class="column-title">{{ location.siteCount }}</div>
                    <div class="column-subtitle">Total Sites</div>
                  </div>
                </div>
                <div class="column">
                  <div class="column-content">
                    <div class="column-title">
                      {{ location.baseYearTotalIdentified }}/{{ location.baseYearTotalEnrolled }}
                    </div>
                    <div class="column-subtitle">Identified/Enrolled</div>
                  </div>
                </div>
                <div class="column">
                  <div class="is-flex column-content flex-justify--center">
                    <div class="isp-padding is-flex">
                      <es-eligibility-marker
                        v-if="location.baseYearISP"
                        :value="location.baseYearISP"
                        size="small"
                        style="padding-right: 0.5rem"/>
                    </div>
                    <div class="isp-box">
                      <div class="column-title">{{ typeof location.baseYearISP === 'number' ? location.baseYearISP.toFixed(2) : '--' }}</div>
                      <div class="column-subtitle">ISP %</div>
                    </div>
                  </div>
                </div>
                <div class="column">
                  <div class="column-content">
                    <div class="column-title">{{ typeof location.baseYearCalculatedISP === 'number' ? location.baseYearCalculatedISP.toFixed(2) : '--' }}</div>
                    <div class="column-subtitle">Calculated ISP %</div>
                  </div>
                </div>
                <div class="column">
                  <div class="column-content">
                    <div class="column-title">
                      <span class="is-inline-block w-25 has-text-right">
                        {{ !location.participationPrevNew ?
                        '-- | --' : location.participationPrevNew }}
                      </span>
                    </div>
                    <div class="column-subtitle">Participation Previous | New</div>
                  </div>
                </div>
                <div class="column">
                  <div class="column-content">
                    <div class="column-title nowrap">
                      <span class="text-nowrap"
                          title="Submitted"
                          v-if="location.electionStatus === 'S'">
                        <font-awesome-icon :icon="['fas', 'dot-circle']"
                              class="has-text-info"/>
                        Submitted
                      </span>
                      <span class="text-nowrap"
                          title="Pending"
                          v-if="location.electionStatus === 'P'">
                        <font-awesome-icon :icon="['fas', 'exclamation-circle']"
                          class="has-text-warning" />
                        Pending
                      </span>
                      <span class="text-nowrap"
                          title="Accepted"
                          v-if="location.electionStatus === 'A'">
                        <font-awesome-icon :icon="['fas', 'check-circle']"
                          class="has-text-success" />
                        Accepted
                      </span>
                      <span
                          title="Rejected"
                          v-if="location.electionStatus === 'R'">
                        <font-awesome-icon :icon="['fas', 'ban']" class="has-text-danger" />
                        Rejected
                      </span>
                    </div>
                    <div class="column-subtitle">Election Status</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-if="cluster" style="padding-bottom: 1rem">
            <cep-details-sites
              :clusterId="stateId"
              :cluster="cluster"
              :locationGroupId="locationGroupId"
              :schoolYear="schoolYear"
              :sortField="sortSitesField"
              :sortOrder="sortSitesOrder"
              :columnHeaders="columnHeadersSites"
              @updateSorter="updateSorter"
            />
          </div>
          <div style="padding-bottom: 1rem">
            <cep-details-programs
              :cepTab="cepTab"
              :cluster="cluster"
              :location="location"
              :schoolYear="schoolYear"
              :locationGroupId="locationGroupId"
              :stateId="stateId"
            />
          </div>
          <div>
            <cep-details-students
              :cepTab="cepTab"
              :cluster="cluster"
              :location="location"
              :schoolYear="schoolYear"
              :locationGroupId="locationGroupId"
              :stateId="stateId"
              :sortField="sortStudentsField"
              :sortOrder="sortStudentsOrder"
              :columnHeaders="columnHeadersStudents"
              @updateSorter="updateSorter"
              @updateStudentProgramHeaders="updateStudentProgramHeaders"
            />
          </div>
        </div>
      </div>
    </div>
    <cep-legend />
  </div>
</template>

<script>
import axios from 'axios';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faAngleDoubleLeft,
} from '@fortawesome/pro-light-svg-icons';
import {
  mapActions,
  mapGetters,
} from 'vuex';
import {
  faBan,
  faCheckCircle,
  faDotCircle,
  faExclamationCircle,
} from '@fortawesome/pro-solid-svg-icons';

import CepLegend from '@/components/CepLegend.vue';
import CepDetailsPrograms from '@/components/CepDetailsPrograms.vue';
import CepDetailsSites from '@/components/CepDetailsSites.vue';
import CepDetailsStudents from '@/components/CepDetailsStudents.vue';
import EsEligibilityMarker from '@/components/EsEligibilityMarker.vue';
import EsHeader from '@/components/EsHeader.vue';
import EsLoader from '@/components/EsLoader.vue';
import FieldLabels from '@/mixins/FieldLabels';

library.add(
  faAngleDoubleLeft,
  faBan,
  faCheckCircle,
  faDotCircle,
  faExclamationCircle,
);

export default {
  mixins: [
    FieldLabels,
  ],
  components: {
    CepLegend,
    CepDetailsPrograms,
    CepDetailsSites,
    CepDetailsStudents,
    EsEligibilityMarker,
    EsHeader,
    EsLoader,
  },
  props: {
    cepSummary: {
      type: Object,
    },
    cepTab: {
      type: String,
    },
    cluster: {
      type: Boolean,
      default: false,
    },
    loadingSummary: {
      type: Boolean,
    },
    loadingSummaryError: {
      type: Boolean,
    },
    locationGroupId: {
      type: [String, Number],
    },
    schoolYear: {
      type: Number,
    },
    stateId: {
      type: Number,
    },
    sortSitesField: {
      type: String,
      default: 'locationName',
    },
    sortSitesOrder: {
      type: String,
      default: 'ASC',
    },
    sortStudentsField: {
      type: String,
      default: 'stateStudentId',
    },
    sortStudentsOrder: {
      type: String,
      default: 'ASC',
    },
  },
  data() {
    return {
      loading: true,
      loadingError: null,
      location: {},
      clusterHistoricalData: [],
      columnHeadersStudents: [],
      columnHeadersSites: [],
    };
  },
  async created() {
    await this.loadPersonTypes();
    this.getLocationSummary();
    this.columnHeadersStudents = [
      {
        header: 'School Code',
        field: 'schoolCode',
        order: null,
        sortable: true,
      },
      {
        header: 'Student ID',
        field: 'stateStudentId',
        order: 'ASC',
        sortable: true,
      },
      {
        header: 'Count',
        field: 'programCount',
        order: null,
        sortable: true,
      },
    ];
    this.columnHeadersSites = [
      {
        header: null,
        field: null,
      },
      {
        header: 'School Code',
        field: 'schoolCode',
        order: null,
        sortable: true,
      },
      {
        header: 'Location Name',
        field: 'locationName',
        order: 'ASC',
        sortable: true,
      },
      {
        header: 'Total Identified',
        field: 'totalIdentified',
        order: null,
        sortable: true,
      },
      {
        header: 'Total Enrolled',
        field: 'totalEnrolled',
        order: null,
        sortable: true,
      },
      {
        header: 'ISP %',
        field: 'baseYearISP',
        order: null,
        sortable: true,
      },
    ];
  },
  computed: {
    ...mapGetters('lookups', [
      'defaultPersonType',
    ]),
    identifiedEnrolledRatio() {
      return `${this.location.baseYearTotalIdentified} / ${this.location.baseYearTotalEnrolled}`;
    },
    getSitesSorter() {
      return this.columnHeadersSites.find(header => header.order);
    },
    getStudentsSorter() {
      return this.columnHeadersStudents.find(header => header.order);
    },
    searchParams() {
      const params = {};

      if (this.cluster) {
        params.cluster = this.cluster;
      }
      if (this.stateId) {
        params.stateId = this.stateId;
      }
      if (this.locationGroupId) {
        params.locationGroupId = this.locationGroupId;
      }
      if (this.schoolYear) {
        params.schoolYear = this.schoolYear;
      }
      if (this.clusterHistoricalData) {
        params.years = this.clusterHistoricalData.map(({ schoolYear }) => schoolYear);
      }
      if (this.getSitesSorter) {
        params.sortSitesField = this.getSitesSorter.field;
        params.sortSitesOrder = this.getSitesSorter.order;
      }
      if (this.getStudentsSorter) {
        params.sortStudentsField = this.getStudentsSorter.field;
        params.sortStudentsOrder = this.getStudentsSorter.order;
      }
      return params;
    },
  },
  watch: {
    schoolYear() {
      this.getLocationSummary();
    },
  },
  methods: {
    ...mapActions('lookups', [
      'loadPersonTypes',
    ]),
    async getLocationSummary() {
      try {
        this.loading = true;
        this.loadingError = false;

        let schoolYearCluster;
        if (this.clusterHistoricalData && this.clusterHistoricalData.length !== 0 && this.cluster) {
          schoolYearCluster = this.clusterHistoricalData.find(cluster => cluster.schoolYear === this.schoolYear);
          if (schoolYearCluster) {
            const { query } = this.$router.currentRoute;
            this.$router.replace({ params: { stateId: schoolYearCluster.clusterId }, query });
          }
        }

        let url = `cep/${this.locationGroupId}/elections/${this.schoolYear}/cluster-summary?`;
        if (schoolYearCluster) {
          url += `clusterId=${schoolYearCluster.clusterId}`;
        } else {
          url += this.cluster
            ? `clusterId=${this.stateId}`
            : `schoolId=${this.stateId}`;
        }
        const res = await axios.get(url);

        this.location = res.data;
        if (this.location) {
          this.clusterHistoricalData = this.location.clusterHistoricalData;
          this.updateSorter();
        }
      } catch (e) {
        this.loadingError = true;
      } finally {
        this.loading = false;
      }
    },
    updateSorter() {
      this.$router.replace({ query: this.searchParams });
    },
    updateStudentProgramHeaders(programs) {
      this.columnHeadersStudents = [
        {
          header: `${this.getFieldLabel(`PER:${this.defaultPersonType}:DIRECTMATCH:SCHOOL_CODE`)} Code`,
          field: 'schoolCode',
          order: null,
          sortable: true,
        },
        {
          header: 'Student ID',
          field: 'stateStudentId',
          order: null,
          sortable: true,
        },
      ];
      programs.forEach(program => this.columnHeadersStudents.push(
        {
          header: program,
          field: null,
          order: null,
          sortable: false,
        },
      ));
      this.columnHeadersStudents.push(
        {
          header: 'Count',
          field: 'programCount',
          order: null,
          sortable: true,
        },
      );
    },
  },
};
</script>

<style lang="sass" scoped>
  .detail-item
    padding: 0.5rem 1rem
    min-width: 165px
    border-left: 1px solid #ccc
  .profile-overview
    .columns
      height: 100%
      .column
        border-left: 1px solid #d9d9d9
        height: 100%
        .column-content
          .column-title
            font-size: 1.15rem
            color: #000
            font-weight: bold
          .column-subtitle
            font-size: .85rem
            color: #888
            white-space: nowrap
</style>
